<template>
  <teleport to="body">
    <transition name="modal">
      <div v-if="visible" class="modal-overlay" @click.self="$emit('close')">
        <div class="modal-container" @keydown.esc="$emit('close')">
          <!-- Header -->
          <div class="modal-header">
            <div class="modal-icon">{{ code.icon }}</div>
            <div class="modal-header-text">
              <h2 class="modal-title">{{ code.name }}</h2>
              <div class="modal-meta">
                <span class="modal-category">{{ code.category }}</span>
                <span class="modal-type-code">🐍 Code</span>
                <span v-if="code.author" class="modal-author">👤 {{ code.author }}</span>
                <span class="modal-stat">👁️ {{ code.views || 0 }}</span>
                <span class="modal-stat">📥 {{ localDownloads }}</span>
              </div>
            </div>
            <button class="modal-close" @click="$emit('close')" title="Close">✕</button>
          </div>

          <!-- Body -->
          <div class="modal-body">
            <p class="modal-desc">{{ code.description }}</p>

            <!-- Parameters -->
            <div v-if="code.params && code.params.length" class="modal-section">
              <h3 class="section-label">⚙️ Parameters</h3>
              <div class="params-list">
                <div v-for="p in code.params" :key="p.name" class="param-item">
                  <span class="param-name">{{ p.name }}</span>
                  <span class="param-type">{{ p.type }}</span>
                  <span class="param-desc">{{ p.desc }}</span>
                  <span v-if="p.default" class="param-default">default: {{ p.default }}</span>
                </div>
              </div>
            </div>

            <!-- Code Preview -->
            <div class="modal-section">
              <h3 class="section-label" @click="showCode = !showCode" style="cursor: pointer">
                🐍 Code {{ showCode ? '▼' : '▶' }}
                <span class="code-lines" v-if="code.code">{{ code.code.split('\n').length }} lines</span>
              </h3>
              <div v-if="showCode" class="code-preview-block">
                <pre class="code-preview-text"><code ref="codeBlock" class="language-python">{{ code.code }}</code></pre>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer">
            <button class="btn-import" @click="handleImport">
              📥 Import Code
            </button>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
const API_BASE = window.location.origin

// highlight.js CDN 로드
let hljsLoaded = false
function loadHighlightJS() {
  if (hljsLoaded) return Promise.resolve()
  return new Promise((resolve) => {
    // CSS
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = 'https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.9.0/styles/atom-one-dark.min.css'
    document.head.appendChild(link)
    // JS
    const script = document.createElement('script')
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.9.0/highlight.min.js'
    script.onload = () => {
      // Python language
      const py = document.createElement('script')
      py.src = 'https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.9.0/languages/python.min.js'
      py.onload = () => { hljsLoaded = true; resolve() }
      document.head.appendChild(py)
    }
    document.head.appendChild(script)
  })
}

export default {
  name: 'CodeModal',
  props: {
    visible: { type: Boolean, default: false },
    code: { type: Object, default: () => ({}) },
  },
  emits: ['close', 'downloaded'],
  data() {
    return {
      localDownloads: (this.code && this.code.downloads) || 0,
      showCode: false,
    }
  },
  watch: {
    code(nv) {
      this.localDownloads = (nv && nv.downloads) || 0
      this.showCode = false
    },
    showCode(v) {
      if (v) this.$nextTick(() => this.highlightCode())
    },
    visible(v) {
      if (v) {
        document.body.style.overflow = 'hidden'
        window.addEventListener('keydown', this.handleEsc)
      } else {
        document.body.style.overflow = ''
        window.removeEventListener('keydown', this.handleEsc)
      }
    },
  },
  methods: {
    handleEsc(e) {
      if (e.key === 'Escape') this.$emit('close')
    },
    async highlightCode() {
      await loadHighlightJS()
      if (this.$refs.codeBlock && window.hljs) {
        window.hljs.highlightElement(this.$refs.codeBlock)
      }
    },
    async handleImport() {
      const exportData = {
        name: this.code.name,
        description: this.code.description,
        code: this.code.code || '',
        params: this.code.params || [],
      }

      // Desktop app (QWebEngineView) → codeimport: URL
      const b64 = btoa(unescape(encodeURIComponent(JSON.stringify(exportData))))
      window.location.href = 'codeimport:' + b64

      // Record download
      fetch(`${API_BASE}/api/codes/${this.code.id}/download`, { method: 'POST' })
      this.localDownloads += 1
      this.$emit('downloaded', this.code.id)
    },
  },
  beforeUnmount() {
    document.body.style.overflow = ''
    window.removeEventListener('keydown', this.handleEsc)
  },
}
</script>

<style scoped>
.modal-overlay {
  position: fixed; inset: 0; z-index: 2000;
  display: flex; align-items: center; justify-content: center;
  background: rgba(0, 0, 0, 0.65); backdrop-filter: blur(8px); padding: 24px;
}
.modal-container {
  background: var(--base); border-radius: 18px; border: 1px solid var(--surface2);
  max-width: 720px; width: 100%; max-height: 85vh;
  display: flex; flex-direction: column;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.5); overflow: hidden;
}
.modal-header {
  display: flex; align-items: flex-start; gap: 16px;
  padding: 28px 28px 20px; border-bottom: 1px solid var(--surface1);
}
.modal-icon { font-size: 40px; line-height: 1; }
.modal-header-text { flex: 1; }
.modal-title { font-size: 22px; font-weight: 800; color: var(--text); margin-bottom: 8px; }
.modal-meta { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.modal-category {
  font-size: 12px; color: var(--lavender);
  background: rgba(144, 160, 208, 0.1); padding: 4px 10px; border-radius: 6px;
}
.modal-type-code {
  font-size: 11px; padding: 3px 8px; border-radius: 6px; font-weight: 600;
  color: var(--green); background: rgba(166, 227, 161, 0.12);
}
.modal-stat { font-size: 12px; color: var(--overlay1); }

.modal-author {
  font-size: 12px;
  color: var(--overlay1);
  background: rgba(127, 132, 156, 0.1);
  padding: 3px 10px;
  border-radius: 6px;
}
.modal-close {
  background: none; border: none; color: var(--overlay0);
  font-size: 20px; cursor: pointer; padding: 4px; border-radius: 6px;
  transition: all var(--transition);
}
.modal-close:hover { background: var(--surface1); color: var(--text); }
.modal-body { flex: 1; padding: 24px 28px; overflow-y: auto; }
.modal-desc { font-size: 14px; color: var(--subtext1); line-height: 1.7; margin-bottom: 24px; }
.modal-section { margin-bottom: 24px; }
.section-label {
  font-size: 13px; font-weight: 700; color: var(--green);
  margin-bottom: 12px; text-transform: uppercase; letter-spacing: 0.5px;
  display: flex; align-items: center; gap: 8px;
}
.code-lines {
  font-size: 11px; color: var(--overlay0); font-weight: 400;
  text-transform: none; letter-spacing: 0;
}

/* Params */
.params-list { display: flex; flex-direction: column; gap: 8px; }
.param-item {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  background: var(--mantle); border: 1px solid var(--surface1);
  border-radius: 8px; padding: 10px 14px;
}
.param-name {
  font-family: 'Courier New', monospace; font-weight: 700;
  font-size: 13px; color: var(--yellow);
}
.param-type {
  font-size: 11px; color: var(--blue); background: rgba(137, 180, 250, 0.1);
  padding: 2px 8px; border-radius: 4px;
}
.param-desc { font-size: 12px; color: var(--subtext0); flex: 1; }
.param-default {
  font-size: 11px; color: var(--overlay0); font-style: italic;
}

/* Code Preview */
.code-preview-block {
  background: var(--mantle); border: 1px solid var(--surface1);
  border-radius: 10px; padding: 16px 20px; border-left: 3px solid var(--green);
}
.code-preview-text {
  font-size: 12px; color: var(--subtext0); line-height: 1.7;
  white-space: pre-wrap; word-break: break-word;
  font-family: 'Courier New', monospace;
  max-height: 400px; overflow-y: auto;
  margin: 0;
}
.code-preview-text code {
  background: transparent !important;
  padding: 0 !important;
  font-size: inherit;
}

/* Footer */
.modal-footer {
  padding: 16px 28px; border-top: 1px solid var(--surface1);
  display: flex; justify-content: flex-end;
}
.btn-import {
  background: linear-gradient(135deg, var(--green), var(--teal));
  color: var(--base); border: none; padding: 12px 28px; border-radius: 10px;
  font-size: 14px; font-weight: 700; cursor: pointer; transition: all var(--transition);
}
.btn-import:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(166, 227, 161, 0.3);
}

/* Transitions */
.modal-enter-active, .modal-leave-active { transition: opacity 0.25s ease; }
.modal-enter-active .modal-container, .modal-leave-active .modal-container {
  transition: transform 0.25s ease, opacity 0.25s ease;
}
.modal-enter-from, .modal-leave-to { opacity: 0; }
.modal-enter-from .modal-container { transform: scale(0.95) translateY(20px); }
.modal-leave-to .modal-container { transform: scale(0.95) translateY(20px); opacity: 0; }
</style>
